<template>
  <div id="content-wrapper" class="cau-hinh-otp otp-scope">
    <div class="otp-page-head">
      <h5 class="otp-page-title">Cấu hình bảo mật OTP</h5>
      <p class="otp-page-sub">
        Thay đổi có hiệu lực ngay sau khi lưu. Giá trị ngoài khoảng cho phép sẽ bị từ chối.
      </p>
    </div>

    <div class="row">
      <div
        v-for="f in fields"
        :key="f.key"
        class="col-md-6 col-xs-12"
      >
        <b-form-group
          :label="f.label"
          label-class="font-weight-bold"
          :label-for="'input-' + f.key"
          :description="`${f.hint} (từ ${f.min} đến ${f.max})`"
        >
          <b-form-input
            :id="'input-' + f.key"
            v-model.number="config[f.key]"
            type="number"
            :min="f.min"
            :max="f.max"
          />
        </b-form-group>
      </div>
    </div>

    <!-- Mục 5: 2 công tắc push/TOTP toàn hệ thống. Không được tắt cả 2 (BE reject tổ hợp chết). -->
    <hr class="section-divider my-4" />
    <!-- Callout ràng buộc nghiệp vụ: reskin làm rõ điều kiện, KHÔNG tự ép trạng thái (BE vẫn là nơi reject). -->
    <div class="otp-callout otp-callout--warning" role="note">
      <svg viewBox="0 0 24 24" aria-hidden="true" class="otp-callout__ic">
        <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
          fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span>Không được tắt đồng thời cả hai công tắc bên dưới. Tắt cả hai sẽ khiến người dùng không còn cách xác thực - hệ thống sẽ từ chối lưu.</span>
    </div>
    <div class="form-row-toggle">
      <b-form-checkbox v-model="config.pushApprovalEnabled" switch>
        <span class="font-weight-bold">Cho phép duyệt đăng nhập qua ứng dụng (Push Approval)</span>
      </b-form-checkbox>
      <small class="text-muted">Bật để người dùng duyệt đăng nhập bằng ứng dụng thay vì gõ mã OTP.</small>
    </div>
    <div class="form-row-toggle">
      <b-form-checkbox v-model="config.allowTotpFallback" switch>
        <span class="font-weight-bold">Cho phép nhập mã OTP trên web (Fallback TOTP)</span>
      </b-form-checkbox>
      <small class="text-muted">
        Tắt để buộc duyệt qua ứng dụng (chế độ bảo mật cao). Không thể tắt đồng thời với công tắc Push ở trên.
      </small>
    </div>

    <!-- Admin reset 2FA step-up (2026-09-14): công tắc độc lập (không thuộc ràng buộc tổ hợp chết push/fallback). -->
    <hr class="section-divider my-4" />
    <div class="form-row-toggle">
      <b-form-checkbox v-model="config.adminResetStepUpEnabled" switch>
        <span class="font-weight-bold">Yêu cầu quản trị viên nhập OTP khi reset 2FA cho người dùng</span>
      </b-form-checkbox>
      <small class="text-muted">
        Bật (khuyến nghị) để quản trị viên phải nhập mã OTP của chính mình mỗi lần reset 2FA cho tài khoản khác.
        Tắt để bỏ bước nhập OTP - quản trị viên vẫn phải đã bật 2FA thì mới reset được.
      </small>
    </div>

    <!-- Yêu cầu sinh trắc học khi Duyệt đăng nhập (2026-09-16): công tắc độc lập (không thuộc ràng buộc tổ hợp chết). -->
    <hr class="section-divider my-4" />
    <div class="form-row-toggle">
      <b-form-checkbox v-model="config.requireBiometricOnApprovalEnabled" switch>
        <span class="font-weight-bold">Yêu cầu xác thực sinh trắc học khi Duyệt đăng nhập trên ứng dụng</span>
      </b-form-checkbox>
      <small class="text-muted">
        Bật (khuyến nghị) để ứng dụng buộc xác thực sinh trắc học (vân tay/khuôn mặt) trước khi duyệt một yêu cầu đăng nhập.
        Tắt để bỏ bước sinh trắc học - ứng dụng vẫn ký duyệt được (bảo mật vẫn dựa trên khoá thiết bị). Thiết bị không có sinh trắc học luôn bỏ qua bước này.
      </small>
    </div>

    <div class="save-bar mt-4 d-flex clearfix">
      <base-button
        button="Lưu cấu hình"
        @click="save"
        :disabled="isLoading"
      ></base-button>
    </div>
    <BaseLoading :is-loading="isLoading" />
  </div>
</template>

<script>
import BaseLoading from "../../common/BaseLoading";

// Phase 7B-1 UC13: màn admin cấu hình bảo mật OTP. GET khi mount, PUT khi lưu (BE validate bound + reload live).
// Đọc response.data.succeeded/.data (common module resolve response.data, camelCase).
export default {
  name: "CauHinhBaoMatOtp",
  components: { BaseLoading },
  data() {
    return {
      isLoading: false,
      config: {
        skewSeconds: 30,
        failThreshold: 5,
        failWindowSeconds: 300,
        lockoutSeconds: 900,
        maxDevicePerUser: 3,
        // Mục 5: 2 công tắc push/TOTP (bool). GET echo giá trị effective; PUT gửi kèm this.config.
        pushApprovalEnabled: false,
        allowTotpFallback: true,
        // Push Autofill UX Phase 08 (2026-08-15) - runtime toggle countdown auto-complete.
        // 0 = auto NGAY (hành vi trước Phase 08, rollback path); 1..10 = hiện overlay countdown + nút Huỷ.
        // OPTIONAL (int? nullable BE, KONGMING FIND #1) - client cũ không gửi field này thì BE giữ singleton hiện tại.
        pushAutoCompleteDelaySeconds: 3,
        // Admin reset 2FA step-up (2026-09-14): bool. GET echo effective; PUT gửi kèm. Default true = bắt admin
        // nhập OTP khi reset 2FA cho user khác (hành vi hiện tại). false = bỏ bước nhập OTP (guard 2FA admin vẫn giữ).
        adminResetStepUpEnabled: true,
        // Yêu cầu sinh trắc học khi Duyệt đăng nhập (2026-09-16): bool. GET echo effective; PUT gửi kèm. Default
        // true = app buộc biometric trước khi ký duyệt (hành vi hiện tại). false = bỏ bước biometric (app vẫn ký được).
        requireBiometricOnApprovalEnabled: true,
      },
      fields: [
        { key: "skewSeconds", label: "Độ lệch thời gian (skew, giây)", min: 0, max: 30, hint: "Dung sai đồng bộ thời gian TOTP" },
        { key: "failThreshold", label: "Số lần sai tối đa / người dùng", min: 1, max: 100, hint: "Vượt ngưỡng sẽ khoá tạm" },
        { key: "failWindowSeconds", label: "Cửa sổ đếm sai (giây)", min: 30, max: 3600, hint: "Khoảng thời gian đếm số lần sai" },
        { key: "lockoutSeconds", label: "Thời gian khoá (giây)", min: 0, max: 86400, hint: "Thời gian khoá sau khi vượt ngưỡng" },
        { key: "maxDevicePerUser", label: "Số thiết bị tối đa / người dùng", min: 1, max: 10, hint: "Giới hạn thiết bị 2FA đăng ký" },
        // Push Autofill UX Phase 08 (2026-08-15): countdown auto-complete sau approve. 0 = auto NGAY (rollback UX).
        { key: "pushAutoCompleteDelaySeconds", label: "Đếm ngược tự đăng nhập sau duyệt (giây)", min: 0, max: 10, hint: "0 = tự đăng nhập ngay khi duyệt; 1-10 = hiện đếm ngược + nút Huỷ" },
      ],
    };
  },
  created() {
    this.loadConfig();
  },
  methods: {
    loadConfig() {
      this.isLoading = true;
      this.$store
        .dispatch("common/getOtpConfig")
        .then((res) => {
          this.isLoading = false;
          if (res.succeeded && res.data) {
            this.config = { ...this.config, ...res.data };
          }
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isLoading = true;
      this.$store
        .dispatch("common/updateOtpConfig", this.config)
        .then((res) => {
          this.isLoading = false;
          if (res.succeeded) {
            if (res.data) this.config = { ...this.config, ...res.data };
            this.$toasted.clear();
            this.$toasted.global.showSuccessMessage({
              message: "Lưu cấu hình bảo mật OTP thành công",
            });
          }
        })
        .catch((error) => {
          this.isLoading = false;
          this.$toasted.clear();
          this.$toasted.global.showErrorMessage({
            message: (error && error.data && error.data.message) || "Lưu cấu hình thất bại",
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
// Reskin OTP (Phase 2): overlay design token --otp-* + typography. Layout/logic giữ nguyên.
// Layout full-width grid 2 cột (col-md-6) cho 5 field cấu hình; toggle push/TOTP giữ 1 hàng full để mô tả rõ.
.cau-hinh-otp {
  font-family: var(--otp-sans);
  font-size: 14px;
  color: var(--otp-ink);

  // Tiêu đề trang: font display Lexend, mô tả muted.
  .otp-page-head {
    margin-bottom: 20px;
  }
  .otp-page-title {
    font-family: var(--otp-display);
    font-weight: 600;
    font-size: 20px;
    color: var(--otp-ink);
    margin: 0 0 6px;
  }
  .otp-page-sub {
    color: var(--otp-muted);
    font-size: 13.5px;
    margin: 0;
  }

  // Callout ràng buộc nghiệp vụ (màu + icon + chữ = đạt WCAG, không dựa mỗi màu).
  .otp-callout {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: var(--otp-radius-sm);
    font-size: 13.5px;
    line-height: 1.5;
    margin-bottom: 18px;
    .otp-callout__ic {
      width: 18px;
      height: 18px;
      flex: none;
      margin-top: 1px;
    }
    &--warning {
      background: var(--otp-warning-bg);
      color: var(--otp-warning);
      border: 1px solid var(--otp-warning);
    }
  }

  // Line ngăn cách giữa nhóm field cấu hình và nhóm toggle - dùng token border.
  .section-divider {
    border: 0;
    border-top: 1px solid var(--otp-border);
    opacity: 1;
  }

  .form-row-toggle {
    margin-bottom: 14px;
    display: flex;
    flex-direction: column;
    small {
      margin-top: 2px;
      margin-left: 2.2rem;
      color: var(--otp-muted);
    }
  }

  // Wrapper nút Lưu - clearfix xử lý BaseButton float-left, đẩy nút xuống rõ khỏi toggle phía trên.
  .save-bar {
    padding-top: 4px;
    &::after {
      content: "";
      display: table;
      clear: both;
    }
  }

  // Ẩn spinner input number đồng đều 5 field cấu hình (Chrome/Firefox) - tránh field max lớn hiện spinner rõ.
  ::v-deep input[type="number"] {
    -moz-appearance: textfield;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
